import { object, string, date, ObjectSchema, boolean } from 'yup';
import { subDays, format } from 'date-fns';
import { dateFormat, helpers } from 'design-system';

import { creditCard, achPayment } from 'src/constants/paymentTypes';

export default function makeValidationSchema(
  minDate: Date,
  maxDate: Date,
): ObjectSchema {
  const { adjustToDateInstance } = dateFormat;

  const minDateString = format(minDate, 'dd/MM/yyyy');
  const maxDateString = format(maxDate, 'dd/MM/yyyy');
  const alphanumericRegEx = RegExp('^[0-9a-zA-Z\\s]+$');
  const numericRegEx = RegExp('^[0-9]+$');

  return object().shape({
    /** Shared fields */
    type: string().required('Tipo de pago es obligatorio'),
    policyStartDate: date()
      .transform((_, originalValue) => adjustToDateInstance(originalValue))
      .min(subDays(minDate, 1), `Fecha mínima aceptada: ${minDateString}`)
      .max(maxDate, `Fecha máxima aceptada: ${maxDateString}`)
      .typeError('Formato de fecha no válido')
      .required('La fecha de inicio de vigencia es requerida'),

    collectionAuthorization: boolean(),

    /** ACH Data fields */
    accountType: string().when('type', {
      is: achPayment,
      then: string().required('El campo tipo de cuenta es requerido'),
    }),
    accountNumber: string().when('type', {
      is: achPayment,
      then: string()
        .required('El campo número de cuenta es requerido')
        .matches(
          numericRegEx,
          'El campo de número de cuenta solo debe contener dígitos',
        )
        .min(
          9,
          'El campo número de la cuenta debe tener longitud mínima de 9 dígitos',
        )
        .max(
          20,
          'El campo número de la cuenta debe tener longitud máxima de 20 dígitos',
        ),
    }),
    accountBankId: string().when('type', {
      is: achPayment,
      then: string().required('Por favor proporcione el banco'),
    }),
    accountOwnerName: string().when('type', {
      is: achPayment,
      then: string()
        .required('El campo titular de la cuenta es requerido')
        .matches(
          alphanumericRegEx,
          'El campo titular de la cuenta debe ser alfanumérico',
        )
        .min(
          2,
          'El campo titular de la cuenta debe tener longitud mínima de 2 caracteres',
        )
        .max(
          50,
          'El campo titular de la cuenta debe tener longitud máxima de 50 caracteres',
        ),
    }),

    /** TDC Data fields */
    cardName: string().when('type', {
      is: creditCard,
      then: string().required('El nombre en la tarjeta es requerido'),
      otherwise: string(),
    }),
    paymentDayNumber: string().when('type', {
      is: creditCard,
      then: string().required('El día de inicio de pago es requerido'),
      otherwise: string(),
    }),
    cardNumber: string().when('type', {
      is: creditCard,
      then: string()
        .test(
          'is-invalid',
          'Número de tarjeta no es válido',
          helpers.validateCardNumber,
        )
        .required('El número de la tarjeta es requerido')
        .min(15, 'Número de tarjeta no es válido'),
    }),
    expirationDate: string().when('type', {
      is: creditCard,
      then: string()
        .test(
          'is-expired',
          'La tarjeta se encuentra expirada',
          (value: string): boolean => {
            return (
              Boolean(value) &&
              value.length >= 5 &&
              !helpers.creditCardExpired(value)
            );
          },
        )
        .length(5, 'Fecha de expiración inválida')
        .required('La fecha de expiración de la tarjeta es requerida'),
    }),
    securityCode: string().when('type', {
      is: creditCard,
      then: string().when('cardNumber', {
        is: (cardNumber) => /^(34|37)/.test(cardNumber),
        then: string()
          .required('El código de seguridad es requerido')
          .length(4, 'El código debe contener 4 caracteres'),
        otherwise: string()
          .required('El código de seguridad es requerido')
          .length(3, 'El código debe contener 3 caracteres'),
      }),
    }),
  });
}
